import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { useNotification } from "~/Hooks/useNotification";
import { corporateCMSApi, post, remove } from "~/Services";
import { corporateHost, magpHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownDataCorporate } from "~/Utils/drilldownDataDum";
import { renderActionsColumn, renderDateColumn, renderPublishedColumn } from "~/Utils/TableRenderFunction";
import { fields, fieldsReady } from "./constants";
import { useNewsDocumentDetails } from "./News.hook";

export const NewsTable = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string | undefined>();
  const [drilldownData, setDrilldownData] = useState<any>(null);
  const [fieldData, setFieldData] = useState<any>();
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [selectedPublished, setSelectedPublished] = useState<any>("");
  const publishedData: TypeData[] = [
    {
      label: "Yes",
      value: true,
    },
    {
      label: "No",
      value: false,
    },
  ];
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    await fieldsReady;
    setFieldData(fields);
  }

  useEffect(() => {
    const fetchDrilldownData = async () => {
      const data = await drilldownDataCorporate();
      setDrilldownData(data);
    };

    fetchDrilldownData();
  }, []);

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useNewsDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  useEffect(() => {
    if (searchValue) {
      const lowerCasedSearch = decodeHTML(searchValue).toLowerCase();

      const filteredData = data.filter((item: any) => {
        const title = item?.field_news_title_en ? item.field_news_title_en.toString().toLowerCase() : "";
        const date = item?.field_news_date ? item?.field_news_date?.toString()?.toLowerCase() : "";

        return decodeHTML(title).includes(lowerCasedSearch) || decodeHTML(date).includes(lowerCasedSearch);
      });
      setSortedData(filteredData);
      setTotalRecords(filteredData.length);
      setCurrentPage(0);
    } else {
      setSortedData(data);
      setTotalRecords(data.length);
      setCurrentPage(0);
    }
  }, [data, searchValue]);
  useEffect(() => {
    let filteredData = [...data];
    if (selectedPublished !== "") {
      const isPublished = selectedPublished === true;
      filteredData = filteredData.filter((item: any) => item.published === isPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [selectedPublished, data]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `/api/news/allfields`;
    corporateCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const handleDelete = async (id: string) => {
    remove(`${magpHost}/api/faq-details/${id}`)
      .then((res) => {
        getRecords();
      })
      .catch((err) => {});
  };

  const columns: TableColumnsType = [
    {
      title: "Title",
      dataIndex: "field_news_title_en",
      key: "field_news_title_en",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder: orderByName === "field_news_title_en" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Date",
      dataIndex: "field_news_date",
      key: "field_news_date",
      render: (text) => renderDateColumn(text),
      sorter: true,
      sortOrder: orderByName === "field_news_date" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder: orderByName === "published" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      fixed: "right",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder: orderByName === "created_at" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Last Update",
      dataIndex: "updated_at",
      key: "updated_at",
      fixed: "right",
      render: (value: any, record: any) => {
        if (value && record.created_at && value === record.created_at) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder: orderByName === "updated_at" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/corporate/media-centre/news/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  const handleTableSort = (column: any) => {
    const newSortOrder = orderByName === column.dataIndex && sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);
    setOrderByName(newSortOrder ? column.dataIndex : undefined);

    if (newSortOrder) {
      const tempData = [...sortedData];
      setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, newSortOrder, column.dataIndex)));
    } else {
      setSortedData([...sortedData]);
    }
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Media Center",
      subModule: "news",
      type: "DEDICATED",
      application: "MAHB_CORPORATE",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAHB_CORPORATE",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "MAHB_CORPORATE_Media Center_News",
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        } else {
          navigate("/dedicated/status-information?activeTab=draft");
        }
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteAwardsRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${corporateHost}/api/newscard/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }
  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  console.log("news-drilldown", drilldownData);
  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(column) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  field_news_title_en: decodeHTML(d.field_news_title_en),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={pageSize}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search by Title and Date"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          pageSize={pageSize}
          total={totalRecords}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger={true}
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteAwardsRequest}
        defaultReviewNotes="Kindly review this Media Center"
      />
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={_get(drilldownData, _get(drilldown, "subModule", "news"))}
        data={drilldown}
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  const valueA = a[sortBy] ?? "";
  const valueB = b[sortBy] ?? "";

  if (["field_news_date", "field_news_cards_created_at", "field_news_cards_updated_at"].includes(sortBy)) {
    const dateA = valueA ? new Date(valueA).getTime() : 0;
    const dateB = valueB ? new Date(valueB).getTime() : 0;
    if (order === "desc") {
      return dateA < dateB ? 1 : -1;
    } else {
      return dateA > dateB ? 1 : -1;
    }
  }
  if (sortBy === "field_news_title_en") {
    const strA = valueA.toString().toLowerCase();
    const strB = valueB.toString().toLowerCase();
    if (order === "desc") {
      return strA < strB ? 1 : -1;
    } else {
      return strA > strB ? 1 : -1;
    }
  }

  if (sortBy === "published") {
    if (order === "desc") {
      return valueA === valueB ? 0 : valueA ? -1 : 1;
    } else {
      return valueA === valueB ? 0 : valueA ? 1 : -1;
    }
  }

  if (order === "desc") {
    return valueA < valueB ? 1 : -1;
  } else {
    return valueA > valueB ? 1 : -1;
  }
}
